import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    n: "01",
    title: "Home & Office Moving",
    desc: "Safe, efficient and hassle-free residential removals and office relocations across Belfast and the UK.",
    image: "/photos/photo3.jpg",
    span: "md:col-span-7 md:row-span-2",
    tall: true,
  },
  {
    n: "02",
    title: "Doorstep Delivery",
    desc: "Fast, secure collection and drop-offs for single items or full loads, delivered right to your door.",
    image: "/photos/photo2.jpg",
    span: "md:col-span-5",
  },
  {
    n: "03",
    title: "Van Hire",
    desc: "Flexible van hire solutions with professional drivers, tailored to your specific schedule.",
    image:
      "/photos/van-hire.jpg",
    span: "md:col-span-5",
  },
  {
    n: "04",
    title: "Packing & Handling",
    desc: "Careful packing, safe handling of fragile items and complete peace of mind — door to door.",
    image: "/photos/photo1.jpg",
    span: "md:col-span-12",
  },
];

export default function Services() {
  return (
    <section id="services" className="bg-[var(--jb-bone)] py-24 md:py-32" data-testid="services-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid md:grid-cols-12 items-end gap-8 mb-14 md:mb-20"
        >
          <div className="md:col-span-1 mono-cap text-[var(--jb-rust)]">(Services)</div>
          <h2 className="md:col-span-8 serif text-[var(--jb-navy)] text-[clamp(40px,6vw,88px)] leading-[0.98] tracking-tight">
            A full spectrum of <span className="italic">moving</span> & <br /> logistics services.
          </h2>
          <div className="md:col-span-3 text-[15px] text-[var(--jb-muted)] leading-[1.55]">
            From a single box or furniture to a full home — we plan, pack, and deliver so you don't have to.
          </div>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-4 md:gap-6">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`relative group overflow-hidden bg-[var(--jb-navy)] text-[var(--jb-bone)] ${s.span} ${
                s.tall ? "min-h-[540px]" : "min-h-[300px] md:min-h-[380px]"
              }`}
              data-testid={`service-card-${s.n}`}
            >
            <img
                src={s.image}
                alt={s.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--jb-navy-2)] via-[var(--jb-navy-2)]/40 to-transparent" />
              <div className="relative h-full p-6 md:p-10 flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <span className="mono-cap opacity-80">{s.n} / 04</span>
                  <ArrowUpRight className="w-6 h-6 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
                </div>
                <div>
                  <h3 className="serif text-[clamp(28px,3.4vw,52px)] leading-[1.02] tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-4 max-w-[420px] text-[15px] text-[var(--jb-bone)]/85 leading-[1.55]">
                    {s.desc}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
